import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { enablePush, pushPermission, pushSupported, isIOS, isStandalone, autoPushBlocked } from "../api/push";
import { usePushEligibility } from "../hooks/usePushEligibility";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ru } from "../locales/ru";

const PwaContext = createContext(null);
let deferredInstall = null;
window.addEventListener("beforeinstallprompt", e => { e.preventDefault(); deferredInstall = e; window.dispatchEvent(new Event("install-prompt-ready")); });
const RECOVER_KEY = "poehali_push_recover";
const INSTALL_KEY = "poputka_install_dismissed";

export const PwaProvider = ({ children }) => {
  const { user } = useAuth();
  const { eligible, milestone } = usePushEligibility();
  const [autoBlocked, setAutoBlocked] = useState(autoPushBlocked);
  const [standalone, setStandalone] = useState(isStandalone);
  const [prompt, setPrompt] = useState(deferredInstall);
  const [sheet, setSheet] = useState(null);
  const [permission, setPermission] = useState(pushPermission);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [dismissedAt, setDismissedAt] = useState(() => {
    const saved = localStorage.getItem(INSTALL_KEY);
    if (saved === "1") { localStorage.setItem(INSTALL_KEY, String(Date.now())); return Date.now(); }
    return Number(saved || 0);
  });
  const recovering = useRef(false);
  const previousPermission = useRef(pushPermission());
  const showPushInstructions = useCallback(() => { localStorage.setItem(RECOVER_KEY, "1"); setSheet("push"); }, []);
  useEffect(() => {
    const ready = () => setPrompt(deferredInstall);
    const installed = () => { deferredInstall = null; setPrompt(null); setStandalone(true); setSheet(null); };
    const display = () => setStandalone(isStandalone());
    const media = window.matchMedia("(display-mode: standalone)");
    window.addEventListener("install-prompt-ready", ready);
    window.addEventListener("appinstalled", installed);
    media.addEventListener("change", display);
    return () => { window.removeEventListener("install-prompt-ready", ready); window.removeEventListener("appinstalled", installed); media.removeEventListener("change", display); };
  }, []);
  useEffect(() => {
    const refresh = () => setAutoBlocked(autoPushBlocked());
    window.addEventListener("push-state-changed", refresh); window.addEventListener("storage", refresh);
    return () => { window.removeEventListener("push-state-changed", refresh); window.removeEventListener("storage", refresh); };
  }, []);
  useEffect(() => {
    if (!user || !pushSupported() || (isIOS() && !standalone)) return;
    const recheck = async () => {
      if (document.visibilityState === "hidden") return;
      const next = pushPermission(); setPermission(next);
      if (next === "denied" || previousPermission.current === "denied") localStorage.setItem(RECOVER_KEY, "1");
      previousPermission.current = next;
      if (next !== "granted" || localStorage.getItem(RECOVER_KEY) !== "1" || recovering.current) return;
      recovering.current = true;
      try { await enablePush(); localStorage.removeItem(RECOVER_KEY); setSheet(s => s === "push" ? null : s); toast.success(ru.pwa.pushOn); window.dispatchEvent(new Event("push-state-changed")); }
      catch { toast.error(ru.errors.generic); }
      finally { recovering.current = false; }
    };
    recheck();
    window.addEventListener("focus", recheck); document.addEventListener("visibilitychange", recheck); window.addEventListener("push-state-changed", recheck);
    return () => { window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); window.removeEventListener("push-state-changed", recheck); };
  }, [user, standalone]);
  const dismissInstall = () => { const at = Date.now(); localStorage.setItem(INSTALL_KEY, String(at)); setDismissedAt(at); };
  const install = async () => {
    if (!prompt) { setSheet("install"); return; }
    await prompt.prompt(); const choice = await prompt.userChoice;
    deferredInstall = null; setPrompt(null);
    if (choice.outcome === "accepted") setStandalone(true);
    else dismissInstall();
  };
  const instructions = sheet === "push" ? standalone && isIOS() ? ru.pwa.deniedIOS : standalone && /android/i.test(navigator.userAgent) ? ru.pwa.deniedAndroid : ru.pwa.deniedChrome : isIOS() ? ru.pwa.iosHint : ru.pwa.chromeInstallHint;
  return <PwaContext.Provider value={{ eligible, milestone, autoBlocked, needsInstall: isIOS() && !standalone, standalone, permission, bannerHeight, setBannerHeight, prompt, showPushInstructions, install, dismissInstall, showInstallBanner: !standalone && Date.now() - dismissedAt >= 14 * 86400000 }}>
    {children}
    <Dialog open={!!sheet} onOpenChange={open => { if (!open) setSheet(null); }}><DialogContent data-testid="pwa-instruction-sheet" className="rounded-2xl max-w-[440px] w-[calc(100%-24px)]">
      <DialogTitle data-testid="pwa-instruction-title">{sheet === "push" ? ru.pwa.pushTitle : ru.pwa.installTitle}</DialogTitle>
      <DialogDescription data-testid="pwa-instructions" className="text-base leading-relaxed text-black py-3">{instructions}</DialogDescription>
      {sheet === "push" && <p data-testid="push-return-hint" className="text-xs text-gray-500">{ru.pwa.deniedHint}</p>}
      <button data-testid="pwa-instructions-close" onClick={() => setSheet(null)} className="h-11 rounded-full bg-black text-white font-semibold">{ru.pwa.instructionClose}</button>
    </DialogContent></Dialog>
  </PwaContext.Provider>;
};
export const usePwa = () => useContext(PwaContext);
